import React, { useState } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import { useDataTable } from '../hooks/useDataTable';
import DataTable from '../components/shared/DataTable';
import PageHeader from '../components/shared/PageHeader';
import StatusBadge from '../components/shared/StatusBadge';
import FilterBar from '../components/shared/FilterBar';
import ConfirmModal from '../components/shared/ConfirmModal';
import CompanyForm from './CompanyForm';
import { useModal } from '../hooks/useModal';
import { useAuth } from '../../context/AuthContext';
import ErrorBoundary from '../../components/ErrorBoundary';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import { showServerError, showSuccess, showError } from '../../utils/toastHelper';

function CompanyManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const {
    isOpen: isCompanyDrawerOpen,
    open: openCompanyDrawer,
    close: closeCompanyDrawer
  } = useModal();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // SUPER_ADMIN kontrolü
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const {
    data: companies,
    loading,
    pagination,
    handlePageChange,
    handleLimitChange,
    handleSearch,
    handleFilterChange,
    filters,
    refresh
  } = useDataTable('/companies', {
    initialFilters: {
      search: '',
      status: 'all'
    }
  });

  const handleClearFilters = () => {
    handleFilterChange('search', '');
    handleFilterChange('status', 'all');
  };

  const handleStatusFilterChange = (value) => {
    if (value === 'all') {
      handleFilterChange('status', 'all');
    } else {
      handleFilterChange('status', value.toUpperCase());
    }
  };

  const handleOpenDrawer = (company = null) => {
    setSelectedCompany(company);
    openCompanyDrawer();
  };

  const handleCloseDrawer = (shouldRefresh = false) => {
    closeCompanyDrawer();
    setSelectedCompany(null);
    if (shouldRefresh) {
      refresh();
    }
  };

  const handleDeleteCompany = (id) => {
    setCompanyToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!companyToDelete) return;

    try {
      setActionLoading(true);
      const response = await apiRequest(`/companies/${companyToDelete}`, {
        method: 'DELETE'
      });

      if (response && response.success) {
        showSuccess(response.message || 'Şirket başarıyla pasif duruma getirildi');
        setShowDeleteConfirm(false);
        setCompanyToDelete(null);
        refresh();
      } else {
        throw new Error((response && response.message) || 'Şirket silinirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Failed to delete company:', error);
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Şirket silinirken bir hata oluştu');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR');
  };

  // Columns Configuration
  const columns = [
    {
      header: 'Şirket Adı',
      accessor: 'name',
      render: (row) => (
        <span className="fw-bold text-truncate d-inline-block" style={{ maxWidth: '150px' }}>
          {row.name}
        </span>
      )
    },
    {
      header: 'Açıklama',
      accessor: 'description',
      render: (row) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: '180px' }}>
          {row.description || '-'}
        </span>
      )
    },
    {
      header: 'Durum',
      accessor: 'status',
      render: (row) => <StatusBadge status={row.status} />
    },
    {
      header: 'Abonelik Sayısı',
      accessor: 'subscriptionCount',
      render: (row) => row.subscriptionCount
    },
    {
      header: 'Aktif Kullanıcı',
      accessor: 'activeUserCount',
      render: (row) => row.activeUserCount
    },
    {
      header: 'Oluşturma Tarihi',
      accessor: 'createdAt',
      render: (row) => formatDate(row.createdAt)
    },
    {
      header: 'İşlemler',
      accessor: 'actions',
      render: (row) => (
        <div className="dropdown" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-sm btn-light text-muted" data-bs-toggle="dropdown">
            <i className="fas fa-ellipsis-v"></i>
          </button>
          <ul className="dropdown-menu dropdown-menu-end border-0 shadow">
            <li>
              <button className="dropdown-item" onClick={() => handleOpenDrawer(row)}>
                <i className="fas fa-edit fa-fw me-2"></i> Düzenle
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={() => navigate(`/admin/companies/${row.id}/stats`)}>
                <i className="fas fa-chart-bar fa-fw me-2"></i> İstatistikler
              </button>
            </li>
            {row.status === 'ACTIVE' && (
              <li>
                <button className="dropdown-item text-danger" onClick={() => handleDeleteCompany(row.id)}>
                  <i className="fas fa-ban fa-fw me-2"></i> Pasife Al
                </button>
              </li>
            )}
          </ul>
        </div>
      )
    }
  ];

  if (!isSuperAdmin) {
    return (
      <div className="container-fluid p-0">
        <div className="alert alert-danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Bu sayfaya erişim yetkiniz bulunmamaktadır
        </div>
      </div>
    );
  }

  // Show full page loader on initial load
  if (loading && (!companies || companies.length === 0)) {
    return (
      <div className="container-fluid p-0">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <LoadingSpinner message="Şirketler yükleniyor..." size="medium" />
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="container-fluid p-0">
        <PageHeader
          title="Şirket Yönetimi"
          description="Sistemdeki tüm şirketleri buradan yönetebilirsiniz"
          breadcrumbs={[{ label: 'Admin' }, { label: 'Şirketler' }]}
          actions={
            <button className="btn btn-primary" onClick={() => handleOpenDrawer()}>
              <i className="fas fa-plus me-2"></i> Yeni Şirket Ekle
            </button>
          }
        />

        {/* Filters */}
        <FilterBar
          filters={[
            {
              type: 'select',
              key: 'status',
              label: 'Durum',
              defaultValue: 'all',
              options: [
                { value: 'all', label: 'Tüm Durumlar' },
                { value: 'ACTIVE', label: 'Aktif' },
                { value: 'INACTIVE', label: 'Pasif' }
              ]
            }
          ]}
          activeFilters={filters}
          onFilterChange={(key, value) => {
            if (key === 'status') {
              handleStatusFilterChange(value);
            } else {
              handleFilterChange(key, value);
            }
          }}
          onClearFilters={handleClearFilters}
          searchPlaceholder="Şirket adı ara..."
          showSearch={true}
          showClearButton={true}
        />

        {/* Content */}
        <DataTable
          tableId="companies"
          columns={columns}
          data={companies}
          loading={loading}
          pagination={pagination}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          onRowClick={handleOpenDrawer}
          emptyState={{
            title: 'Şirket Bulunamadı',
            message: 'Yeni bir şirket eklemek için "Yeni Şirket Ekle" butonuna tıklayın',
            icon: 'building',
            action: (
              <button className="btn btn-primary btn-sm" onClick={() => handleOpenDrawer()}>
                <i className="fas fa-plus me-2"></i> Yeni Şirket Ekle
              </button>
            )
          }}
        />

        {/* Company Drawer */}
        <CompanyForm
          isOpen={isCompanyDrawerOpen}
          onClose={() => handleCloseDrawer()}
          company={selectedCompany}
          onSuccess={() => handleCloseDrawer(true)}
        />

        <ConfirmModal
          show={showDeleteConfirm}
          onClose={() => {
            setShowDeleteConfirm(false);
            setCompanyToDelete(null);
          }}
          onConfirm={confirmDelete}
          title="Şirketi Pasife Al"
          message="Bu şirketi silmek istediğinize emin misiniz? Bu işlem geri alınamaz ve şirket pasif duruma geçecektir."
          confirmText="Pasife Al"
          cancelText="İptal"
          variant="danger"
          loading={actionLoading}
        />
      </div>
    </ErrorBoundary>
  );
}

export default CompanyManagement; 